// Drawing App - Texture Manager
// Generates and caches 4 texture types for brush overlay

import { TextureType } from './types';

const TEXTURE_SIZE = 256;

export class TextureManager {
  private textures: Map<TextureType, OffscreenCanvas> = new Map();
  private thumbnails: Map<TextureType, string> = new Map();

  constructor() {
    this.generateAll();
  }

  private generateAll(): void {
    this.generatePaperGrain();
    this.generateCanvasWeave();
    this.generateNoise();
    this.generateHalftone();
  }

  getTexture(type: TextureType): OffscreenCanvas | null {
    if (type === 'none') return null;
    return this.textures.get(type) || null;
  }

  getThumbnail(type: TextureType): string {
    return this.thumbnails.get(type) || '';
  }

  getAvailableTypes(): { type: TextureType; label: string }[] {
    return [
      { type: 'none', label: '없음' },
      { type: 'paper-grain', label: '종이결' },
      { type: 'canvas-weave', label: '캔버스' },
      { type: 'noise', label: '노이즈' },
      { type: 'halftone', label: '하프톤' },
    ];
  }

  private createCanvas(): [OffscreenCanvas, OffscreenCanvasRenderingContext2D] {
    const canvas = new OffscreenCanvas(TEXTURE_SIZE, TEXTURE_SIZE);
    const ctx = canvas.getContext('2d')!;
    return [canvas, ctx];
  }

  private storeThumbnail(type: TextureType, canvas: OffscreenCanvas): void {
    // Create a small thumbnail for UI preview
    const thumb = new OffscreenCanvas(48, 48);
    const ctx = thumb.getContext('2d')!;
    ctx.drawImage(canvas, 0, 0, 48, 48);
    // Convert to dataURL via regular canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = 48;
    tempCanvas.height = 48;
    const tempCtx = tempCanvas.getContext('2d')!;
    tempCtx.drawImage(thumb, 0, 0);
    this.thumbnails.set(type, tempCanvas.toDataURL('image/png'));
  }

  private generatePaperGrain(): void {
    const [canvas, ctx] = this.createCanvas();
    const imageData = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
    const data = imageData.data;

    for (let i = 0; i < TEXTURE_SIZE * TEXTURE_SIZE; i++) {
      // Multi-frequency noise for paper texture
      const x = i % TEXTURE_SIZE;
      const y = Math.floor(i / TEXTURE_SIZE);
      const fine = Math.random() * 40;
      const medium = Math.sin(x * 0.3) * Math.cos(y * 0.3) * 20;
      const coarse = Math.sin(x * 0.05 + y * 0.07) * 30;
      const value = 180 + fine + medium + coarse;
      const clamped = Math.max(0, Math.min(255, value));

      const idx = i * 4;
      data[idx] = clamped;
      data[idx + 1] = clamped;
      data[idx + 2] = clamped;
      data[idx + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    this.textures.set('paper-grain', canvas);
    this.storeThumbnail('paper-grain', canvas);
  }

  private generateCanvasWeave(): void {
    const [canvas, ctx] = this.createCanvas();
    const imageData = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
    const data = imageData.data;

    for (let i = 0; i < TEXTURE_SIZE * TEXTURE_SIZE; i++) {
      const x = i % TEXTURE_SIZE;
      const y = Math.floor(i / TEXTURE_SIZE);

      // Weave pattern using sin waves
      const warp = Math.sin(x * Math.PI / 4) * 0.5 + 0.5;
      const weft = Math.sin(y * Math.PI / 4) * 0.5 + 0.5;
      const weave = (warp + weft) / 2;
      const noise = Math.random() * 15;
      const value = 160 + weave * 60 + noise;
      const clamped = Math.max(0, Math.min(255, value));

      const idx = i * 4;
      data[idx] = clamped;
      data[idx + 1] = clamped;
      data[idx + 2] = clamped;
      data[idx + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    this.textures.set('canvas-weave', canvas);
    this.storeThumbnail('canvas-weave', canvas);
  }

  private generateNoise(): void {
    const [canvas, ctx] = this.createCanvas();
    const imageData = ctx.createImageData(TEXTURE_SIZE, TEXTURE_SIZE);
    const data = imageData.data;

    for (let i = 0; i < TEXTURE_SIZE * TEXTURE_SIZE; i++) {
      const value = Math.random() * 255;
      const idx = i * 4;
      data[idx] = value;
      data[idx + 1] = value;
      data[idx + 2] = value;
      data[idx + 3] = 255;
    }

    ctx.putImageData(imageData, 0, 0);
    this.textures.set('noise', canvas);
    this.storeThumbnail('noise', canvas);
  }

  private generateHalftone(): void {
    const [canvas, ctx] = this.createCanvas();

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, TEXTURE_SIZE, TEXTURE_SIZE);

    // Grid of dots
    ctx.fillStyle = '#000000';
    const dotSpacing = 8;
    const maxRadius = dotSpacing * 0.4;

    for (let y = 0; y < TEXTURE_SIZE; y += dotSpacing) {
      for (let x = 0; x < TEXTURE_SIZE; x += dotSpacing) {
        // Vary dot size slightly for organic feel
        const radius = maxRadius * (0.6 + Math.random() * 0.4);
        ctx.beginPath();
        ctx.arc(x + dotSpacing / 2, y + dotSpacing / 2, radius, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    this.textures.set('halftone', canvas);
    this.storeThumbnail('halftone', canvas);
  }
}
